'use client';

import React, { useEffect, useState } from 'react';
import { Palette, X } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

const PREVIEW_SKINS = [
  { id: 'skin-zero', code: 'SKIN-00', label: 'shadcn Default', tier: 'Free' },
  { id: 'oma-original', code: 'OMA-01', label: 'White Swan', tier: '$4,999' },
] as const;

export function TemplatePreviewBar({
  initialPreviewOverride,
}: {
  initialPreviewOverride?: string | null;
}) {
  const { designId } = useTenant();
  const [visible, setVisible] = useState(Boolean(initialPreviewOverride));
  const [activeId, setActiveId] = useState(initialPreviewOverride || designId);

  useEffect(() => {
    setActiveId(initialPreviewOverride || designId);
    if (initialPreviewOverride) {
      setVisible(true);
      try {
        window.sessionStorage.setItem('raisonne-preview-design', initialPreviewOverride);
      } catch {
        // ignore storage errors
      }
      return;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const qDesign = params.get('design')?.trim().toLowerCase();
      const qPreview = params.get('preview')?.trim();
      const stored = window.sessionStorage.getItem('raisonne-preview-design');
      if (qDesign === 'reset' || qDesign === 'default' || qDesign === 'clear') {
        window.sessionStorage.removeItem('raisonne-preview-design');
        setVisible(false);
      } else if (qDesign || qPreview === '1' || stored) {
        setVisible(true);
        if (qDesign) {
          setActiveId(qDesign);
          window.sessionStorage.setItem('raisonne-preview-design', qDesign);
        } else if (stored) {
          setActiveId(stored);
        }
      }
    } catch {
      // ignore
    }
  }, [initialPreviewOverride, designId]);

  if (!visible) return null;

  const switchSkin = (targetId: string) => {
    try {
      window.sessionStorage.setItem('raisonne-preview-design', targetId);
      document.cookie = `raisonne-preview-design=${encodeURIComponent(targetId)}; path=/; SameSite=Lax`;
      const url = new URL(window.location.href);
      url.searchParams.set('design', targetId);
      window.location.href = url.toString();
    } catch {
      window.location.search = `?design=${encodeURIComponent(targetId)}`;
    }
  };

  const exitPreview = () => {
    try {
      window.sessionStorage.removeItem('raisonne-preview-design');
      document.cookie = 'raisonne-preview-design=; path=/; max-age=0; SameSite=Lax';
      const url = new URL(window.location.href);
      url.searchParams.set('design', 'reset');
      window.location.href = url.toString();
    } catch {
      window.location.search = '?design=reset';
    }
  };

  return (
    <div className="fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2 pointer-events-auto">
      <div className="flex items-center gap-1.5 rounded-full border border-white/15 bg-zinc-950/90 px-3 py-1.5 text-xs text-white shadow-2xl backdrop-blur-md">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
          <Palette className="h-3 w-3 text-emerald-400" />
          <span className="hidden sm:inline">Preview</span>
        </span>

        <div className="flex items-center gap-1">
          {PREVIEW_SKINS.map((skin) => {
            const isCurrent = activeId === skin.id;
            return (
              <button
                key={skin.id}
                type="button"
                onClick={() => switchSkin(skin.id)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  isCurrent
                    ? 'bg-white text-zinc-950 shadow-sm'
                    : 'text-zinc-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <span className="font-mono text-[10px] opacity-70 mr-1">{skin.code}</span>
                <span>{skin.label}</span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={exitPreview}
          title="Exit template preview"
          aria-label="Exit template preview"
          className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
